import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ConfigProvider, Spin } from 'antd';
import antdTheme from './theme/antdTheme';
import api from './libs/api';

import Login from './pages/Login';
import SetPassword from './pages/SetPassword';
import RetailCrm from './pages/RetailCrm';
import RetailFinance from './pages/RetailFinance';
import CommercialFinance from './pages/CommercialFinance';
import MapOfSystems from './pages/MapOfSystems';
import BudgetControl from './pages/BudgetControl';
import CashflowControl from './pages/CashflowControl';
import CashAccount from './pages/CashAccount';
import PpaSystem from './pages/PpaSystem';
import VppRevenue from './pages/VppRevenue';
import Distribution from './pages/Distribution';
import MonthlyFinance from './pages/MonthlyFinance';
import LiabilityList from './pages/LiabilityList';
import UserManagement from './pages/UserManagement';
import Profile from './pages/Profile';
import PowerTracking from './pages/PowerTracking';
import AemoEnergyMarket from './pages/AemoEnergyMarket';
import FcasMarket from './pages/FcasMarket';
import RebateCheatsheet from './pages/RebateCheatsheet';
import ProjectFinance from './pages/ProjectFinance';
import FinancialPlanning from './pages/FinancialPlanning';
import XeroPL from './pages/XeroPL';
import MarketIntelligence from './pages/MarketIntelligence';
import Complaints from './pages/Complaints';
import { getDefaultRoute, getStoredPermissions } from './utils/defaultRoute';

function isAuthenticated() {
  try {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    return Boolean(token || userId);
  } catch (_) {
    return false;
  }
}

function ProtectedRoute() {
  const [status, setStatus] = useState(() => (isAuthenticated() ? 'checking' : 'denied'));

  useEffect(() => {
    if (status !== 'checking') return;
    let alive = true;
    api.get('/api/auth/profile')
      .then(() => { if (alive) setStatus('ok'); })
      .catch(() => { if (alive) setStatus('denied'); })
    return () => { alive = false; };
  }, [status]);

  if (status === 'checking') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafbfc' }}>
        <Spin size="large" />
      </div>
    );
  }
  if (status === 'denied') return <Navigate to="/login" replace />;
  return <Outlet />;
}

// Default redirect: if logged in, send to first allowed sidebar page; otherwise to /login.
function DefaultRedirect() {
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  // Role type as well as permissions: some entries are gated on it, and the
  // sidebar's own test needs both to agree with what the sidebar will show.
  return <Navigate to={getDefaultRoute(getStoredPermissions(), localStorage.getItem('roleType') || '')} replace />;
}

export default function App() {
  return (
    <ConfigProvider theme={antdTheme}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<DefaultRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/set-password" element={<SetPassword />} />

          <Route element={<ProtectedRoute />}>
            <Route path="/cash-account" element={<CashAccount />} />
            <Route path="/monthly-finance" element={<MonthlyFinance />} />
            <Route path="/financial-planning" element={<FinancialPlanning />} />
            <Route path="/liability-list" element={<LiabilityList />} />
            <Route path="/retail-crm" element={<RetailCrm />} />
            <Route path="/retail-finance" element={<RetailFinance />} />
            <Route path="/commercial-finance" element={<CommercialFinance />} />
            <Route path="/map-of-systems" element={<MapOfSystems />} />
            {/* Renamed to Opex Control. The old path stays and the new one
                points at the same page, so existing bookmarks keep working. */}
            <Route path="/budget-control" element={<BudgetControl />} />
            <Route path="/opex-control" element={<BudgetControl />} />
            <Route path="/cashflow-control" element={<CashflowControl />} />
            <Route path="/ppa" element={<PpaSystem />} />
            <Route path="/vpp" element={<VppRevenue />} />
            <Route path="/distribution" element={<Distribution />} />
            <Route path="/power-tracking" element={<PowerTracking />} />
            <Route path="/aemo-energy-market" element={<AemoEnergyMarket />} />
            <Route path="/fcas-market" element={<FcasMarket />} />
            <Route path="/rebate-cheatsheet" element={<RebateCheatsheet />} />
            <Route path="/project-finance" element={<ProjectFinance />} />
            <Route path="/xero-pl" element={<XeroPL />} />
            <Route path="/market-intelligence" element={<MarketIntelligence />} />
            {/* Legacy path — keep existing bookmarks working */}
            <Route path="/stc-stats" element={<MarketIntelligence />} />
            <Route path="/complaints" element={<Complaints />} />
            <Route path="/admin/users" element={<UserManagement />} />
            <Route path="/profile" element={<Profile />} />
          </Route>

          <Route path="*" element={<DefaultRedirect />} />
        </Routes>
      </BrowserRouter>
    </ConfigProvider>
  );
}
